import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarCheck,
  CalendarX,
  Loader2,
  Phone,
  Mail,
  Car,
  Clock,
  CalendarDays,
  CheckCheck,
  CircleDollarSign,
  StickyNote,
  Link as LinkIcon,
  Unlink,
} from "lucide-react";
import AdminNav from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "od_admin_token";

const STATUS_TABS = [
  { id: "pending", label: "Pending", testid: "tab-pending" },
  { id: "confirmed", label: "Confirmed", testid: "tab-confirmed" },
  { id: "declined", label: "Declined", testid: "tab-declined" },
  { id: "all", label: "All", testid: "tab-all" },
];

export default function AdminBookings() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [calendar, setCalendar] = useState({ connected: false });
  const [calLoading, setCalLoading] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [bk, cal] = await Promise.all([
        axios.get(`${API}/admin/bookings`, auth),
        axios.get(`${API}/auth/google/status`),
      ]);
      setBookings(bk.data);
      setCalendar(cal.data);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("calendar");
    if (status === "connected") {
      toast.success("Google Calendar connected!");
      window.history.replaceState({}, "", "/admin/bookings");
    } else if (status === "error") {
      toast.error("Couldn't connect Google Calendar — try again");
      window.history.replaceState({}, "", "/admin/bookings");
    }
  }, []);

  if (!token) return <Navigate to="/admin" replace />;

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const connectCalendar = async () => {
    setCalLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/google/login`, {
        ...auth,
        params: { origin: window.location.origin },
      });
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error("Couldn't start Google sign-in");
        setCalLoading(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start Google sign-in");
      setCalLoading(false);
    }
  };

  const disconnectCalendar = async () => {
    if (!window.confirm("Disconnect Google Calendar? New confirmations won't create events.")) return;
    try {
      await axios.post(`${API}/auth/google/disconnect`, {}, auth);
      setCalendar({ connected: false });
      toast.success("Google Calendar disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const filtered = bookings.filter((b) =>
    tab === "all" ? true : (b.status || "pending") === tab
  );
  const counts = bookings.reduce(
    (acc, b) => {
      const s = b.status || "pending";
      acc[s] = (acc[s] || 0) + 1;
      acc.all = (acc.all || 0) + 1;
      return acc;
    },
    {}
  );

  const handleConfirm = async (id) => {
    setBusyId(id);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${id}/confirm`, {}, auth);
      if (data.warning) toast.warning(data.warning);
      else toast.success("Booking confirmed — calendar invite sent");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to confirm");
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (id) => {
    if (!window.confirm("Decline this booking? The customer will not be notified automatically.")) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/bookings/${id}/decline`, {}, auth);
      toast.success("Booking declined");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to decline");
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkPaid = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/bookings/${id}/mark-paid`, {}, auth);
      toast.success("Marked as paid");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <AdminNav onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
            Customer appointments
          </div>
          <h1 className="font-serif text-4xl text-white font-light tracking-tight">
            Bookings
          </h1>
        </header>

        {/* Google Calendar status banner */}
        <section
          data-testid="calendar-banner"
          className={`mb-8 p-5 lg:p-6 border rounded-sm flex flex-col md:flex-row md:items-center gap-4 justify-between ${
            calendar.connected
              ? "bg-emerald-500/5 border-emerald-500/30"
              : "bg-[#111] border-[#D6B98D]/30"
          }`}
        >
          <div className="flex items-center gap-4">
            <CalendarDays
              className={`w-6 h-6 ${calendar.connected ? "text-emerald-400" : "text-[#D6B98D]"}`}
              strokeWidth={1.5}
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-1">
                Google Calendar
              </div>
              {calendar.connected ? (
                <div>
                  <div className="text-sm text-white">
                    Connected as <span className="text-emerald-300">{calendar.email}</span>
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    Confirmed bookings will appear on this calendar and email customers an invite.
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-sm text-white">Not connected yet</div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    Connect once so confirmed appointments auto-create calendar events + email invites.
                  </div>
                </div>
              )}
            </div>
          </div>
          {calendar.connected ? (
            <button
              onClick={disconnectCalendar}
              data-testid="calendar-disconnect"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#262626] hover:border-red-400 text-red-300 text-[10px] uppercase tracking-[0.25em] font-bold rounded-sm transition-all"
            >
              <Unlink className="w-3.5 h-3.5" /> Disconnect
            </button>
          ) : (
            <button
              onClick={connectCalendar}
              disabled={calLoading}
              data-testid="calendar-connect"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] disabled:opacity-60 text-[10px] uppercase tracking-[0.25em] font-bold rounded-sm transition-all"
            >
              {calLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LinkIcon className="w-3.5 h-3.5" />}
              Connect Google Calendar
            </button>
          )}
        </section>

        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {STATUS_TABS.map((t) => {
            const active = tab === t.id;
            const count = counts[t.id] || 0;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={t.testid}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.25em] rounded-sm transition-all whitespace-nowrap ${
                  active
                    ? "bg-[#D6B98D] text-[#050505] font-bold"
                    : "border border-[#262626] text-white/60 hover:border-[#D6B98D]/50 hover:text-[#D6B98D]"
                }`}
              >
                {t.label}
                <span className={`text-[10px] px-1.5 rounded-sm ${active ? "bg-[#050505]/20" : "bg-[#262626]"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-white/50">
            <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading bookings…
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="empty-bookings"
            className="py-20 px-6 text-center border border-dashed border-[#262626] rounded-sm"
          >
            <CalendarDays className="w-10 h-10 text-[#D6B98D] mx-auto mb-4" strokeWidth={1} />
            <p className="text-white/60 text-sm">No {tab !== "all" ? tab : ""} bookings yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {filtered.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                onConfirm={() => handleConfirm(b.id)}
                onDecline={() => handleDecline(b.id)}
                onMarkPaid={() => handleMarkPaid(b.id)}
                busy={busyId === b.id}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function statusBadge(status) {
  const s = status || "pending";
  const map = {
    pending: { bg: "bg-amber-500/10", text: "text-amber-300", border: "border-amber-500/30", label: "Pending" },
    confirmed: { bg: "bg-emerald-500/10", text: "text-emerald-300", border: "border-emerald-500/30", label: "Confirmed" },
    declined: { bg: "bg-red-500/10", text: "text-red-300", border: "border-red-500/30", label: "Declined" },
    cancelled: { bg: "bg-white/5", text: "text-white/50", border: "border-white/10", label: "Cancelled" },
  };
  const cfg = map[s] || map.pending;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] font-semibold rounded-sm border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      {cfg.label}
    </span>
  );
}

function BookingCard({ booking, onConfirm, onDecline, onMarkPaid, busy }) {
  const isPending = (booking.status || "pending") === "pending";
  const isConfirmed = booking.status === "confirmed";
  const isPaid = booking.payment_status === "paid";

  return (
    <div
      data-testid={`booking-card-${booking.id}`}
      className="p-6 bg-[#111] border border-[#262626] hover:border-[#D6B98D]/40 transition-colors rounded-sm"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-2xl text-white font-light">{booking.full_name}</div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#D6B98D] mt-1">
            {booking.package_name} — ${Number(booking.amount).toFixed(0)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {statusBadge(booking.status)}
          {isPaid && (
            <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/90">Paid</span>
          )}
        </div>
      </div>

      <div className="space-y-2 text-sm text-white/70 font-light mb-5">
        <Row icon={CalendarDays} text={booking.appointment_date} />
        <Row icon={Clock} text={booking.appointment_time} />
        <Row icon={Car} text={booking.vehicle} />
        <Row icon={Phone} text={booking.phone} />
        <Row icon={Mail} text={booking.email} />
        {booking.notes && <Row icon={StickyNote} text={booking.notes} />}
      </div>

      <div className="flex flex-wrap gap-2 pt-4 border-t border-[#262626]">
        {isPending && (
          <>
            <button
              onClick={onConfirm}
              disabled={busy}
              data-testid={`confirm-${booking.id}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] disabled:opacity-60 text-[10px] uppercase tracking-[0.25em] font-bold rounded-sm transition-all"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarCheck className="w-3.5 h-3.5" />}
              Confirm
            </button>
            <button
              onClick={onDecline}
              disabled={busy}
              data-testid={`decline-${booking.id}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#262626] hover:border-red-400 text-red-300 text-[10px] uppercase tracking-[0.25em] font-bold rounded-sm transition-all"
            >
              <CalendarX className="w-3.5 h-3.5" /> Decline
            </button>
          </>
        )}
        {isConfirmed && !isPaid && (
          <button
            onClick={onMarkPaid}
            disabled={busy}
            data-testid={`mark-paid-${booking.id}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#D6B98D] text-[#D6B98D] hover:bg-[#D6B98D] hover:text-[#050505] text-[10px] uppercase tracking-[0.25em] font-bold rounded-sm transition-all"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CircleDollarSign className="w-3.5 h-3.5" />}
            Mark paid (in person)
          </button>
        )}
        {isConfirmed && isPaid && (
          <span className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] text-emerald-300/80">
            <CheckCheck className="w-3.5 h-3.5" /> Completed
          </span>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 mt-0.5 text-[#D6B98D]/70 flex-shrink-0" strokeWidth={1.5} />
      <span className="break-words">{text}</span>
    </div>
  );
}
